import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { BaseCRUDManager } from '../base/BaseCRUDManager';
import AlbumManager from '../AlbumManager';
import { ProjectManager } from '../ProjectManager';
import { generateAlbumKey } from '../../utilities/albumKeyGenerator';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';
import { IRecording } from '../../types/Recording';
import {
  getDemoProjectSettings,
  setDemoProjectSettings,
} from '../../components/admin-nav/utils/demoProjectSettings';

const RECORDINGS_COLLECTION = 'Recordings';

async function ensureIntroAlbumKey(
  recording: IRecording,
  projectId: string,
  recordingId: string,
  demo: boolean
): Promise<string> {
  if (recording.albumKey) {
    return recording.albumKey;
  }

  const generatedAlbumKey = await generateAlbumKey();
  const albumName = recording.file?.title || 'Project Intro';

  if (demo) {
    await DemoDataManager.createAlbum({
      albumKey: generatedAlbumKey,
      name: albumName,
      projectKey: projectId,
      userKey: recording.ownership?.recordingOwner ?? null,
    });
    DemoDataManager.updateRecording(recordingId, { albumKey: generatedAlbumKey });
    return generatedAlbumKey;
  }

  const createdAlbum = await AlbumManager.createAlbum({
    albumKey: generatedAlbumKey,
    name: albumName,
    projectKey: projectId,
    userKey: recording.ownership?.recordingOwner || null,
  });

  const introAlbumKey = createdAlbum.albumKey;
  if (!introAlbumKey) {
    throw new Error('Failed to create album for project intro recording');
  }

  await BaseCRUDManager.update<IRecording>(
    RECORDINGS_COLLECTION,
    recordingId,
    { albumKey: introAlbumKey },
    { idField: 'key', autoTimestamps: false }
  );

  return introAlbumKey;
}

async function persistIntroSettings(
  projectId: string,
  recordingId: string,
  introAlbumKey: string,
  demo: boolean
): Promise<void> {
  if (demo) {
    const existingSettings = getDemoProjectSettings(projectId);
    setDemoProjectSettings(projectId, {
      settings: {
        ...existingSettings.settings,
        introRecordingKey: recordingId,
        introAlbumKey,
      },
    });
    localStorage.setItem(
      `demo_project_settings_version_${projectId}`,
      Date.now().toString()
    );
    return;
  }

  await ProjectManager.updateProjectSettingsFields(projectId, {
    introRecordingKey: recordingId,
    introAlbumKey,
  });
}

/**
 * Assign an orphaned project recording as the one-time project intro.
 */
export async function assignRecordingAsProjectIntro(
  projectId: string,
  recordingId: string
): Promise<void> {
  const demo = isDemoMode();

  if (demo) {
    const recording = DemoDataManager.getRecording(recordingId);

    if (!recording) {
      throw new Error(`No recording found with ID: ${recordingId}`);
    }

    if (recording.project && recording.project !== projectId) {
      throw new Error('Recording does not belong to this project');
    }

    if (recording.pinKey) {
      DemoDataManager.updateRecording(recordingId, { pinKey: '' });
    }

    const introAlbumKey = await ensureIntroAlbumKey(recording, projectId, recordingId, true);
    await persistIntroSettings(projectId, recordingId, introAlbumKey, true);
    return;
  }

  const recordingRef = doc(db, RECORDINGS_COLLECTION, recordingId);
  const recordingSnap = await getDoc(recordingRef);

  if (!recordingSnap.exists()) {
    throw new Error(`No recording found with ID: ${recordingId}`);
  }

  const recording = recordingSnap.data() as IRecording;

  if (recording.project && recording.project !== projectId) {
    throw new Error('Recording does not belong to this project');
  }

  if (recording.pinKey) {
    await BaseCRUDManager.update<IRecording>(
      RECORDINGS_COLLECTION,
      recordingId,
      { pinKey: '' },
      { idField: 'key', autoTimestamps: false }
    );
  }

  const introAlbumKey = await ensureIntroAlbumKey(recording, projectId, recordingId, false);
  await persistIntroSettings(projectId, recordingId, introAlbumKey, false);
}
